import React from 'react';
import { Shield, Heart, Home, Briefcase, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Insurance = () => {
  const insuranceProducts = [
    {
      icon: Heart,
      title: "Health Insurance",
      description: "Comprehensive health coverage for you and your family with access to quality healthcare services.",
      features: ["Hospital coverage", "Outpatient care", "Emergency services", "Prescription coverage"]
    },
    {
      icon: Home,
      title: "Home Insurance",
      description: "Protect your home and belongings from unexpected events with our comprehensive coverage plans.",
      features: ["Property damage", "Theft protection", "Natural disasters", "Liability coverage"]
    },
    {
      icon: Briefcase,
      title: "Business Insurance",
      description: "Safeguard your business assets and operations with tailored commercial insurance solutions.",
      features: ["Business property", "Liability protection", "Business interruption", "Employee coverage"]
    },
    {
      icon: Shield,
      title: "Life Insurance",
      description: "Secure your family's financial future with flexible life insurance plans and investment options.",
      features: ["Term life", "Whole life", "Investment plans", "Critical illness"]
    }
  ];

  const benefits = [
    "24/7 claims support and assistance",
    "Fast and easy claim processing",
    "Flexible premium payment options",
    "Comprehensive coverage packages",
    "Expert financial advisors",
    "No hidden fees or charges"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">Investment Solutions</h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                We help you grow and protect your wealth through expert financial advice, tailored investment portfolios, and innovative market opportunities built on trust and transparency.
              </p>
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg">
                Get a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/163056/computer-buy-money-banknotes-163056.jpeg"
                alt="Investment Solutions"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Insurance Products</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive protection for what matters most to you
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {insuranceProducts.map((product, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    <product.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{product.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>
                  <div className="space-y-2 mb-6">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/3531895/pexels-photo-3531895.jpeg"
                alt="Insurance Benefits"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Our Insurance?</h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We provide comprehensive insurance solutions designed to give you peace of mind and financial security.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    </div>
                    <span className="text-gray-600">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Claims Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple Claims Process</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Filing a claim is easy with our streamlined process
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-3xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Report Your Claim</h3>
              <p className="text-gray-600">Contact us via phone, email, or our mobile app to report your claim anytime, anywhere.</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-3xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Submit Documentation</h3>
              <p className="text-gray-600">Provide the necessary documents and our team will guide you through every step.</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-3xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Receive Settlement</h3>
              <p className="text-gray-600">Once approved, receive your settlement quickly and hassle-free.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Options Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Investment-Linked Plans</h2>
              <p className="text-blue-100 text-lg mb-6 leading-relaxed">
                Combine protection with investment growth through our innovative investment-linked insurance plans.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <span className="text-blue-100">Diversified investment portfolios</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <span className="text-blue-100">Professional fund management</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                  <span className="text-blue-100">Flexible premium options</span>
                </li>
              </ul>
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3">
                Explore Plans
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/3483098/pexels-photo-3483098.jpeg"
                alt="Investment Plans"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Protect What Matters Most</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get personalized insurance advice from our expert advisors
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
            Talk to an Advisor
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Insurance;
